<script setup lang="ts">
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { IPrinter } from '@/interfaces/IPrinter';
  import { IPrintingSettings } from '@/interfaces/IPrintingSettings';
  import { CODEPAGE_CYCLE } from '@/lib/print/test-ticket';
  import { useNativePrintingUi } from '@/composition/printing/use-native-printing-ui';
  import { INativeWizardDevice } from '@/composition/printing/native-wizard-commit';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { TNativeWizardType } from './NativeStepType.vue';

  const props = defineProps<{
    device: INativeWizardDevice;
    type: TNativeWizardType;
    /** Wizard-local ESC/POS codepage (default: the ticket destination's). */
    encoding: string;
  }>();

  const emit = defineEmits<{
    (e: 'confirmed'): void;
    (e: 'encoding-change', encoding: string): void;
    /** "It's a label printer" realization → jump back to the Type step. */
    (e: 'change-type'): void;
  }>();

  const ui = useUi();
  const nativeUi = useNativePrintingUi();
  const tests = nativeUi.tests;

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const printer = computed<IPrinter>(() => ({
    type: props.device.type,
    id: props.device.id,
    name: props.device.name,
  }));

  const testSettings = computed<IPrintingSettings>(() => ({
    ...nativeUi.printing.ticketPrintingSettings.value,
    encoding: props.encoding,
  }));

  const anyTesting = computed(() => tests.testingPrinterId.value !== null);

  type TPrintState = 'idle' | 'printing' | 'done' | 'failed';
  const printState = ref<TPrintState>('idle');

  /** After a successful send, the human answers "¿Salió bien?". */
  const trouble = ref<'none' | 'symbols' | 'nothing'>('none');

  const probing = ref(false);
  const probeResult = ref<'ok' | 'fail' | null>(null);

  async function probeDevice(): Promise<boolean> {
    const strategy = nativeUi.nativeStrategyOrNull();
    if (!strategy || !props.device.host || props.device.port === undefined) {
      return false;
    }
    const result = await strategy.probeNetworkPrinter(
      `${props.device.host}:${props.device.port}`
    );
    return result.reachable;
  }

  async function printTest() {
    if (anyTesting.value || printState.value === 'printing') return;
    printState.value = 'printing';
    probeResult.value = null;
    trouble.value = 'none';

    // Network devices probe INSIDE the test guard (preflight): the whole
    // flow shows one spinner and an unreachable printer fails honestly
    // instead of toasting a fake "sent".
    const preflight =
      props.device.type === 'tcp'
        ? async () => {
            const reachable = await probeDevice();
            if (!reachable) probeResult.value = 'fail';
            return reachable;
          }
        : undefined;

    const sent =
      props.type === 'zpl'
        ? await tests.testSticker(printer.value, preflight)
        : await tests.testEscPos(printer.value, testSettings.value, preflight);

    printState.value = sent ? 'done' : 'failed';
  }

  // #region trouble: símbolos raros (codepage samples, escpos only)

  const printingSamples = ref(false);
  const samplesPrinted = ref(false);

  async function printSamples() {
    if (printingSamples.value || anyTesting.value) return;
    printingSamples.value = true;
    try {
      const sent = await tests.printCodepageSamples(printer.value);
      if (sent) samplesPrinted.value = true;
    } finally {
      printingSamples.value = false;
    }
  }

  function pickSample(index: number) {
    emit('encoding-change', CODEPAGE_CYCLE[index]);
    samplesPrinted.value = false;
    trouble.value = 'none';
    printState.value = 'idle';
    // NOT helpCodepageSaved: the pick is wizard-local until Finish, so the
    // copy must not claim it was saved (cancel would discard it).
    ui.showToast(AlertType.Success, i18nT('printingV2.nativeEncodingNoted'));
  }

  // #endregion

  // #region trouble: no salió nada (transport-aware)

  async function probeManually() {
    if (probing.value) return;
    probing.value = true;
    probeResult.value = null;
    try {
      probeResult.value = (await probeDevice()) ? 'ok' : 'fail';
    } catch (e) {
      console.error('[NativePrinting] wizard trouble probe failed', e);
      probeResult.value = 'fail';
    } finally {
      probing.value = false;
    }
  }

  const relisting = ref(false);

  /** Interactive on purpose: the user is actively troubleshooting. */
  async function relistDevices() {
    if (relisting.value) return;
    relisting.value = true;
    try {
      await nativeUi.reload(true);
    } finally {
      relisting.value = false;
    }
  }

  // #endregion
</script>

<template>
  <div class="native-step-test">
    <div class="test-printer-line">
      <PosUiIcon name="printer" class="test-printer-icon" />
      {{ device.name }}
    </div>

    <!-- Print action + state -->
    <div class="test-main">
      <PosUiButton
        color="primary"
        size="lg"
        icon="printer"
        :loading="printState === 'printing'"
        :disabled="anyTesting && printState !== 'printing'"
        @click="printTest"
      >
        {{ $t('printingV2.testPrintButton') }}
      </PosUiButton>

      <div v-if="printState === 'failed'" class="test-result is-fail">
        <PosUiIcon name="close-circle" class="test-result-icon" />
        <span>{{ $t('printingV2.testFailed') }}</span>
      </div>
    </div>

    <!-- Human confirmation (mandatory before finishing) -->
    <div
      v-if="printState === 'done' && trouble === 'none'"
      class="test-question"
    >
      <div class="test-question-title">{{ $t('printingV2.testQuestion') }}</div>
      <div class="test-question-actions">
        <PosUiButton color="success" icon="check" @click="emit('confirmed')">
          {{ $t('printingV2.testYes') }}
        </PosUiButton>
        <PosUiButton
          color="neutral"
          variant="outline"
          icon="close"
          @click="trouble = type === 'escpos' ? 'symbols' : 'nothing'"
        >
          {{ $t('printingV2.testNo') }}
        </PosUiButton>
      </div>
    </div>

    <!-- Trouble flows -->
    <div
      v-if="trouble !== 'none' || printState === 'failed'"
      class="test-trouble"
    >
      <div v-if="printState === 'done'" class="test-trouble-tabs">
        <button
          v-if="type === 'escpos'"
          type="button"
          class="trouble-tab"
          :class="{ active: trouble === 'symbols' }"
          @click="trouble = 'symbols'"
        >
          {{ $t('printingV2.troubleSymbols') }}
        </button>
        <button
          type="button"
          class="trouble-tab"
          :class="{ active: trouble === 'nothing' }"
          @click="trouble = 'nothing'"
        >
          {{ $t('printingV2.troubleNothing') }}
        </button>
      </div>

      <!-- Codepage samples (escpos only) -->
      <div
        v-if="trouble === 'symbols' && printState === 'done'"
        class="trouble-body"
      >
        <p class="trouble-text">{{ $t('printingV2.helpSymbolsIntro') }}</p>
        <PosUiButton
          color="primary"
          variant="soft"
          size="sm"
          :loading="printingSamples"
          :disabled="anyTesting && !printingSamples"
          @click="printSamples"
        >
          {{ $t('printingV2.helpPrintSamples') }}
        </PosUiButton>
        <template v-if="samplesPrinted">
          <p class="trouble-text">{{ $t('printingV2.helpPickSample') }}</p>
          <div class="trouble-actions">
            <PosUiButton
              v-for="(cp, index) in CODEPAGE_CYCLE"
              :key="cp"
              color="neutral"
              variant="outline"
              size="sm"
              @click="pickSample(index)"
            >
              {{ $t('printingV2.helpSampleN', { n: index + 1 }) }}
            </PosUiButton>
          </div>
        </template>
      </div>

      <!-- Nothing came out: transport-aware checklist -->
      <div
        v-if="trouble === 'nothing' || printState === 'failed'"
        class="trouble-body"
      >
        <!-- An ESC/POS test that produced NOTHING is the Zebra-trap
             signature: ZPL-only label printers silently eat ESC/POS bytes.
             Offer the jump back to the Type step. -->
        <div v-if="type === 'escpos'" class="trouble-zpl-hint">
          <PosUiIcon name="tag-outline" class="trouble-zpl-hint-icon" />
          <span>{{ $t('printingV2.nativeZplHint') }}</span>
          <PosUiButton
            color="neutral"
            variant="outline"
            size="sm"
            @click="emit('change-type')"
          >
            {{ $t('printingV2.nativeChangeType') }}
          </PosUiButton>
        </div>

        <!-- Network: probe + subnet tip -->
        <template v-if="device.type === 'tcp'">
          <ol class="trouble-steps">
            <li>{{ $t('printingV2.helpNoPrintStep1') }}</li>
            <li>{{ $t('printingV2.nativeHelpNetTip') }}</li>
          </ol>
          <div class="trouble-actions">
            <PosUiButton
              color="neutral"
              variant="outline"
              size="sm"
              :loading="probing"
              @click="probeManually"
            >
              {{ $t('printingV2.helpProbe') }}
            </PosUiButton>
          </div>
          <div v-if="probeResult === 'ok'" class="test-result is-ok">
            <PosUiIcon name="check-circle" class="test-result-icon" />
            {{ $t('printingV2.helpProbeOk') }}
          </div>
          <div v-else-if="probeResult === 'fail'" class="test-result is-fail">
            <PosUiIcon name="close-circle" class="test-result-icon" />
            {{ $t('printingV2.helpProbeFail') }}
          </div>
        </template>

        <!-- USB: cable/permission checklist + re-list -->
        <template v-else-if="device.type === 'usb'">
          <ol class="trouble-steps">
            <li>{{ $t('printingV2.nativeUsbCheck1') }}</li>
            <li>{{ $t('printingV2.nativeUsbCheck2') }}</li>
          </ol>
          <div class="trouble-actions">
            <PosUiButton
              color="neutral"
              variant="outline"
              size="sm"
              icon="refresh"
              :loading="relisting"
              @click="relistDevices"
            >
              {{ $t('printingV2.refresh') }}
            </PosUiButton>
          </div>
        </template>

        <!-- Bluetooth: pairing checklist + interactive re-list -->
        <template v-else>
          <ol class="trouble-steps">
            <li>{{ $t('printingV2.nativeBtGuide1') }}</li>
            <li>{{ $t('printingV2.nativeBtGuide2') }}</li>
            <li>{{ $t('printingV2.nativeBtGuide3') }}</li>
          </ol>
          <div class="trouble-actions">
            <PosUiButton
              color="neutral"
              variant="outline"
              size="sm"
              icon="refresh"
              :loading="relisting"
              @click="relistDevices"
            >
              {{ $t('printingV2.refresh') }}
            </PosUiButton>
          </div>
        </template>

        <!-- Escape hatch: a FAILED test must never dead-end the wizard
             (the printer may simply be off — the Device step already
             allowed proceeding unreachable, and this is the only add
             path). The honest-test default stays: this is an explicit
             user acknowledgement, not a fake success. -->
        <div v-if="printState === 'failed'" class="trouble-continue">
          <PosUiButton
            color="neutral"
            variant="outline"
            size="sm"
            icon="arrow-right"
            @click="emit('confirmed')"
          >
            {{ $t('printingV2.nativeContinueAnyway') }}
          </PosUiButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .native-step-test {
    @apply tw-flex tw-flex-col tw-gap-4;

    .test-printer-line {
      @apply tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-neutral-700;

      .test-printer-icon {
        @apply tw-text-lg tw-text-neutral-400;
      }
    }

    .test-main {
      @apply tw-flex tw-flex-col tw-items-start tw-gap-3;
    }

    .test-result {
      @apply tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm;

      &.is-ok {
        @apply tw-bg-emerald-50 tw-text-emerald-700;
      }

      &.is-fail {
        @apply tw-bg-red-50 tw-text-red-700;
      }

      .test-result-icon {
        @apply tw-flex-none tw-text-base;
      }
    }

    .test-question {
      @apply tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-neutral-50 tw-p-4;

      .test-question-title {
        @apply tw-mb-3 tw-text-sm tw-font-semibold tw-text-neutral-800;
      }

      .test-question-actions {
        @apply tw-flex tw-gap-2;
      }
    }

    .test-trouble {
      @apply tw-flex tw-flex-col tw-gap-3;

      .test-trouble-tabs {
        @apply tw-flex tw-gap-0.5 tw-self-start tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

        .trouble-tab {
          @apply tw-rounded-md tw-border tw-border-transparent tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors hover:tw-text-neutral-800;

          &.active {
            @apply tw-border-neutral-200 tw-bg-white tw-font-semibold tw-text-neutral-900 tw-shadow-sm;
          }
        }
      }

      .trouble-body {
        @apply tw-flex tw-flex-col tw-items-start tw-gap-2.5;

        .trouble-text {
          @apply tw-m-0 tw-text-sm tw-text-neutral-600;
        }

        .trouble-steps {
          @apply tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-pl-5 tw-text-sm tw-text-neutral-600;
        }

        .trouble-actions {
          @apply tw-flex tw-flex-wrap tw-gap-2;
        }

        .trouble-continue {
          @apply tw-mt-1 tw-w-full tw-border-t tw-border-neutral-100 tw-pt-3;
        }

        .trouble-zpl-hint {
          @apply tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-lg tw-bg-sky-50 tw-px-3 tw-py-2 tw-text-sm tw-text-sky-800;

          .trouble-zpl-hint-icon {
            @apply tw-flex-none tw-text-base;
          }
        }
      }
    }
  }
</style>
